import { Modal } from '@/components/common/Modal';
import Pagination from '@/components/common/Pagination';
import TooltipTruncate from '@/components/TooltipTruncate';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { usePermissions } from '@/context/PermissionContext';
import ResponsiveDataTable from '@/components/mobileCards/ResponsiveDataTable';
import useRoles from '@/hooks/RolesAndPriviledges/useRoles';
import DeleteModal from '@/pages/Banker-management/Modal/DeleteModal';
import { Role } from '@/types/rolesTypes';

import { formatReadableDateTime } from '@/utils';
import { ColumnDef } from '@tanstack/react-table';
import { Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
const Roles = () => {
  const {
    activeModal,
    setActiveModal,
    deleteId,
    setDeleteId,
    closeModal,
    setCurrentPage,
    search,
    setSearch,
    resetPage,
    setPageSize,
    pageSize,
    totalItems,
    currentPage,
    rolesData,
    isLoading,
    handleDeleteRole,
    sorting,
    handleSortingChange,
  } = useRoles();

  const navigate = useNavigate();
  const { hasModuleAccess } = usePermissions();
  const canUpdate = hasModuleAccess('Roles-Privileges', 'update');
  const canDelete = hasModuleAccess('Roles-Privileges', 'delete');
  const canSeeDetailedView = hasModuleAccess('Roles-Privileges', 'detailedView');

  const administratorsColumns: ColumnDef<Role>[] = [
    {
      accessorKey: 'sno',
      enableSorting: false,
      meta: {
        align: 'center',
      },
      header: 'S. No.',
      cell: ({ row }) => (
        <div className="text-center">{(currentPage - 1) * pageSize + row.index + 1}</div>
      ),
    },
    {
      accessorKey: 'name',
      header: 'Role',
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <TooltipTruncate
          text={row.original?.description}
          maxLength={20}
          id={`tooltip-${row.original?.description}`}
        />
      ),
    },
    {
      accessorKey: 'permission',
      enableSorting: false,
      header: 'Permission',
      meta: {
        align: 'center',
      },
      cell: ({ row }) => {
        const id = row.original?.id;
        return (
          <div className=" flex justify-center items-center">
            <Button
              disabled={!canSeeDetailedView}
              onClick={() => navigate(`/create-roles/${id}/view`)}
            >
              View
            </Button>
          </div>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      meta: {
        align: 'center',
      },
      cell: ({ row }) => (
        <div className="text-center">{formatReadableDateTime(row.original.createdAt)}</div>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      enableSorting: false,
      meta: {
        align: 'center',
      },
      cell: ({ row }) => {
        const { id } = row.original;
        return (
          <div className="flex items-center justify-center gap-2">
            <Button
              variant="link"
              className="text-black"
              disabled={!canUpdate}
              onClick={() => navigate(`/create-roles/${id}/edit`)}
            >
              Edit
            </Button>
            <Button
              variant="destructive"
              disabled={!canDelete}
              onClick={() => {
                setDeleteId(id);
                setActiveModal('deleteModal');
              }}
            >
              Delete
            </Button>
          </div>
        );
      },
    },
  ];
  return (
    <>
      <div className="flex justify-between">
        {' '}
        <div className="relative max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder={`Search by Role Name...`}
            className=" pl-9 text-[#162f34] border-green focus-visible:ring-green"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              resetPage();
            }}
          />
        </div>
      </div>

      <div className="py-2">
        <ResponsiveDataTable
          data={rolesData ?? []}
          columns={administratorsColumns}
          isLoading={isLoading}
          variant="default"
          pinnedKeys={['name', 'createdAt']}
          actionKeys={['permission', 'action']}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
        />
      </div>

      <div className="py-4">
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems || 0}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[10, 25, 50, 100, 500, 1000]}
        />
      </div>

      {activeModal && (
        <Modal
          open={!!activeModal}
          onOpenChange={closeModal}
          title={'Delete Role'}
          closeBtn={<X />}
          style={activeModal === 'qr' && 'max-w-[70dvw]'}
        >
          {activeModal === 'deleteModal' && (
            <DeleteModal
              onCancel={closeModal}
              deleteId={String(deleteId)}
              onConfirm={handleDeleteRole}
              phrase="this Role"
            />
          )}
        </Modal>
      )}
    </>
  );
};

export default Roles;
